/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useMemo } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import MDDatePicker from "components/MDDatePicker";
import MDSelect from "components/MDSelect";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import EventCalendar from "examples/Calendar/EventCalendar";
import Navbar from "examples/Navbars/NavBar";
import Footer from "examples/Footer";
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";

const initAttr = {
  id: "",
  name: "",
  customer_id: "",
  saleman_id: "",
  color: "info",
  date_start: Func.dateNow(),
  time_start: Func.timeNow(),
  date_stop: Func.dateNow(),
  time_stop: Func.timeNow(),
  enable: "Y",
  created_at: "",
  updated_at: "",
};

const initFind = {
  search: "",
  saleman: "ALL",
  date_start: Func.dateFirstMonth(),
  date_stop: Func.dateEndMonth(),
};

const cboColor = [
  { id: "primary", name: "ชมพู" },
  { id: "secondary", name: "เทา" },
  { id: "info", name: "น้ำเงิน" },
  { id: "success", name: "เขียว" },
  { id: "warning", name: "เหลือง" },
  { id: "error", name: "แดง" },
  { id: "light", name: "ขาว" },
  { id: "dark", name: "ดำ" },
];

function Meeting() {
  const mod = "meeting";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [record, setRecord] = useState([]);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalDel, setModalDel] = useState(false);
  const [cboSaleman, setCboSaleman] = useState([]);
  const [cboCustomer, setCboCustomer] = useState([]);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon="notifications"
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);

    FetchData("POST", `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === "ok") {
        setRecord(result.data);
      }
      setLoading(false);
    });
  };

  const open = (item) => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);

    FetchData("GET", `/${mod}/${item.id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setAttr(result.data);
        setModalEdit(true);

        setLoading(false);
      }
    });
  };

  const addConfirm = () => {
    if (attr.customer_id * 1 <= 0) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);

    FetchData("PUT", `/${mod}`, app.token, {
      name: attr.name,
      customer_id: attr.customer_id,
      saleman_id: attr.saleman_id,
      date_start: attr.date_start,
      time_start: attr.time_start,
      date_stop: attr.date_stop,
      time_stop: attr.time_stop,
      color: attr.color,
      enable: attr.enable,
    }).then((result) => {
      setLoading(false);

      if (result.status === "ok") {
        view(find);
        showNotify(t(`alert.addcomplete`), "success");
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), "error");
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const edit = () => {
    setLoading(true);
    setModalEdit(false);

    FetchData("PATCH", `/${mod}/${attr.id}`, app.token, {
      name: attr.name,
      enable: attr.enable,
    }).then((result) => {
      setLoading(false);

      if (result.status === "ok") {
        view(find);
        showNotify(t(`alert.editcomplete`), "success");
      } else {
        setModalEdit(true);
        showNotify(t(`alert.saveerror`), "error");
      }
    });
  };

  const del = () => {
    setLoading(true);
    setModalDel(false);

    FetchData("DELETE", `/${mod}/${attr.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === "ok") {
        view(find);
        showNotify(t(`alert.deletecomplete`), "success");
      } else {
        showNotify(t(`alert.deleteerror`), "error");
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const init = () => {
    FetchData("GET", `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setCboSaleman(result.saleman);
        setCboCustomer(result.customer);
      } else {
        Func.exitApp();
      }
    });
  };

  const btnDel = (item) => {
    return (
      <MDButton
        variant="text"
        color="error"
        size="small"
        iconOnly
        onClick={() => {
          setConfirm(false);
          setAttr(item);
          setModalDel(true);
        }}
      >
        <Icon>delete</Icon> {t(`element.delete`)}
      </MDButton>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component="form"
              role="form"
              onSubmit={(e) => {
                e.preventDefault();
                view(find);
              }}
            >
              <MDInput
                name="searchform"
                fullWidth
                value={find.search}
                type="search"
                onChange={(e) => setFind({ ...find, search: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">{t("element.search")} :</InputAdornment>
                  ),
                  autoComplete: "off",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={2} xl={2}>
            <MDInput
              fullWidth
              value={find.saleman}
              defaultValue="ALL"
              select
              onChange={(e) => view({ ...find, saleman: e.target.value })}
            >
              <MenuItem value="ALL">นักขาย</MenuItem>
              {cboSaleman.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={10} xl={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={2} xl={1}>
            <MDButton
              variant="contained"
              color="dark"
              size="large"
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setAttr(initAttr);
                setConfirm(false);
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            {useMemo(
              () => (
                <EventCalendar
                  initialView="dayGridMonth"
                  events={record}
                  selectable
                  editable
                  datesSet={(arg) => {
                    view({
                      ...find,
                      date_start: Func.dateFormat(arg.view.activeStart),
                      date_stop: Func.dateFormat(arg.view.activeEnd),
                    });
                  }}
                />
              ),
              [record]
            )}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDSelect
            label="ลูกค้า *"
            labelDefault={t(`element.please_select`)}
            data={cboCustomer}
            value={attr.customer_id}
            onChange={(e) => setAttr({ ...attr, customer_id: e.target.value })}
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label="นักขาย *"
            labelDefault={t(`element.please_select`)}
            data={cboSaleman}
            value={attr.saleman_id}
            onChange={(e) => setAttr({ ...attr, saleman_id: e.target.value })}
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label="สี *"
            labelDefault={t(`element.please_select`)}
            data={cboColor}
            value={attr.color}
            onChange={(e) => setAttr({ ...attr, color: e.target.value })}
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={7}>
          <MDDatePicker
            onChange={([date]) => {
              setAttr({ ...attr, date_start: Func.dateFormat(date) });
            }}
            value={new Date(attr.date_start)}
            input={{
              label: "เริ่มต้น",
              variant: "standard",
              fullWidth: true,
            }}
          />
        </Grid>
        <Grid item xs={5}>
          <MDInput
            label="เวลา"
            onChange={(e) => setAttr({ ...attr, time_start: e.target.value })}
            value={attr.time_start}
            variant="standard"
            fullWidth
            type="time"
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={7}>
          <MDDatePicker
            onChange={([date]) => {
              setAttr({ ...attr, date_stop: Func.dateFormat(date) });
            }}
            value={new Date(attr.date_stop)}
            input={{
              label: "สิ้นสุด",
              variant: "standard",
              fullWidth: true,
            }}
          />
        </Grid>
        <Grid item xs={5}>
          <MDInput
            label="เวลา"
            onChange={(e) => setAttr({ ...attr, time_stop: e.target.value })}
            value={attr.time_stop}
            variant="standard"
            fullWidth
            type="time"
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth="xs" keepMounted aria-describedby="dialog-add" fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label="close"
            onClick={() => setModalAdd(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant="text" color="error" onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <MDButton variant="outlined" color="success" onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant="text" color="info" onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderEdit = () => {
    return (
      <Dialog open={modalEdit} maxWidth="xs" keepMounted aria-describedby="dialog-edit" fullWidth>
        <DialogTitle>
          {t(`element.edit`)} #{attr.id}
          <IconButton
            aria-label="close"
            onClick={() => setModalEdit(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant="text" color="error" onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <MDButton variant="outlined" color="success" onClick={edit}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant="text" color="success" onClick={editConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderDelete = () => {
    return (
      <Dialog
        open={modalDel}
        onClose={() => setModalDel(false)}
        maxWidth="xs"
        keepMounted
        aria-describedby="dialog-delete"
        fullWidth
      >
        <DialogTitle>
          {t(`element.delete`)} #{attr.id}
          <IconButton
            aria-label="close"
            onClick={() => setModalDel(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText p={3}>{t(`alert.confirm_delete_title`)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant="text" color="error" onClick={() => setModalDel(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <MDButton variant="gradient" color="error" onClick={del}>
                  <Icon>delete</Icon>&nbsp;{t(`element.deleteconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant="text" color="error" onClick={() => setConfirm(true)}>
              <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, []);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.customer`), t(`menu.meeting`)]} />
      {renderList()}
      {renderAdd()}
      {renderEdit()}
      {renderDelete()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default Meeting;
